import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { type FC, useState } from "react";

import { getPromptTestRun } from "@api/rest/prompt-test-bed";
import { CopyButton } from "@components/CopyButton/CopyButton";

/** Collapsible "what was sent to the LLM" for one run. Fetched only when
 *  opened — the comparison page polls, and prompts are large. */
export const RunLlmInput: FC<{ promptKey: string; runId: number }> = ({ promptKey, runId }) => {
  const [open, setOpen] = useState(false);
  const {
    data: run,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["prompt-test-run-llm-input", promptKey, runId],
    queryFn: () => getPromptTestRun(promptKey, runId),
    enabled: open,
    staleTime: Infinity,
  });
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <div className="mb-3 flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="cursor-pointer flex items-center gap-1.5 self-start text-sm font-medium text-text-secondary hover:text-text-primary"
      >
        <Chevron className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
        <span>What was sent to the LLM</span>
      </button>
      {open &&
        (isPending ? (
          <div className="pl-5 text-xs text-text-tertiary">Loading…</div>
        ) : isError ? (
          <div className="pl-5 text-xs text-text-error-primary">Couldn’t load what was sent.</div>
        ) : run.llm_input_snapshot == null ? (
          <div className="pl-5 text-xs italic text-text-tertiary">
            {run.status === "running"
              ? "Run is still in progress."
              : "Nothing was sent — the model was never called (required inputs were likely missing)."}
          </div>
        ) : (
          <div className="flex flex-col gap-1 pl-5">
            <div className="flex justify-end">
              <CopyButton value={run.llm_input_snapshot} ariaLabel="Copy what was sent to clipboard" />
            </div>
            <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded border border-border-subtle bg-bg-default p-3 font-mono text-xs">
              {run.llm_input_snapshot}
            </pre>
          </div>
        ))}
    </div>
  );
};
